import { useEffect, useState } from 'react';
import {
  getParametresDevis,
  reinitialiserDemandes,
  updateParametresDevis,
} from '../services/parametreDevisService';
import SelectRecherche from '../components/SelectRecherche';
import RemplirParTexte from '../components/RemplirParTexte';
import '../assets/styles/Parametrage.css';

/**
 * Bouton de réinitialisation : toutes les demandes de devis et ce qui en
 * découle quittent les listes (mis à la corbeille, restaurables un par un). Le
 * geste est large — d'où le mot à taper avant, et le bilan chiffré affiché après.
 */
function ReinitialisationDemandes() {
  const [ouverte, setOuverte] = useState(false);
  const [confirmation, setConfirmation] = useState('');
  const [enCours, setEnCours] = useState(false);
  const [message, setMessage] = useState(null);

  async function lancer() {
    setEnCours(true);
    setMessage(null);
    try {
      const res = await reinitialiserDemandes(confirmation);
      setOuverte(false);
      setConfirmation('');
      setMessage({ type: 'success', texte: res.message || 'Réinitialisation faite.' });
    } catch (err) {
      setMessage({ type: 'error', texte: err.message || 'Réinitialisation impossible.' });
    } finally {
      setEnCours(false);
    }
  }

  return (
    <div className="param-card">
      <div className="param-card-header">
        <div>
          <div className="param-section-titre">Réinitialisation des demandes de devis</div>
          <div className="param-card-note">
            Met à la corbeille demandes, devis, contrats, planifications, documents, clients et
            formateurs (restaurables). Catalogue, modèles et réglages restent.
          </div>
        </div>
        <button
          type="button"
          className="param-btn param-btn-danger"
          onClick={() => {
            setOuverte(true);
            setConfirmation('');
            setMessage(null);
          }}
        >
          Réinitialiser
        </button>
      </div>
      {message && (
        <div className="param-card-body">
          <div
            className={`param-form-message ${
              message.type === 'error' ? 'param-form-message-error' : 'param-form-message-success'
            }`}
          >
            {message.texte}
          </div>
        </div>
      )}

      {ouverte && (
        <div className="param-modal-overlay" onClick={() => !enCours && setOuverte(false)}>
          <div className="param-modal param-modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="param-modal-header">
              <h2 className="param-modal-title">Réinitialiser les demandes de devis</h2>
            </div>
            <div className="param-modal-body">
              <p className="param-confirm-text">
                Demandes, devis, contrats, documents, clients et formateurs seront mis à la
                corbeille, où chacun reste restaurable 30 jours. Tapez{' '}
                <strong>SUPPRIMER</strong> pour confirmer.
              </p>
              <label className="param-field">
                <span className="param-label">Confirmation</span>
                <input
                  type="text"
                  className="param-input"
                  value={confirmation}
                  onChange={(e) => setConfirmation(e.target.value)}
                  placeholder="SUPPRIMER"
                  autoComplete="off"
                  disabled={enCours}
                />
              </label>
              {message && message.type === 'error' && (
                <div className="param-form-message param-form-message-error">{message.texte}</div>
              )}
              <div className="param-form-actions">
                <button
                  type="button"
                  className="param-btn"
                  onClick={() => setOuverte(false)}
                  disabled={enCours}
                >
                  Annuler
                </button>
                <button
                  type="button"
                  className="param-btn param-btn-danger-solid"
                  onClick={lancer}
                  disabled={enCours || confirmation.trim().toUpperCase() !== 'SUPPRIMER'}
                >
                  {enCours ? 'Réinitialisation' : 'Tout mettre à la corbeille'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ParametrageDevis() {
  const [parametres, setParametres] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    getParametresDevis()
      .then((res) => setParametres(res.data))
      .catch(() => setError('Impossible de charger le paramétrage de devis.'))
      .finally(() => setLoading(false));
  }, []);

  function handleChange(field) {
    return (e) => {
      const value = e.target.value;
      setParametres((prev) => ({ ...prev, [field]: value }));
      setSuccess(null);
    };
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);

    const payload = {
      ...parametres,
      tauxTva: Number(parametres.tauxTva),
      dureeValiditeJours: Number(parametres.dureeValiditeJours),
    };

    try {
      const res = await updateParametresDevis(payload);
      setParametres(res.data);
      setSuccess('Paramétrage mis à jour.');
    } catch (err) {
      setError(err.message || 'Une erreur est survenue.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="param-page">
      <div className="param-header">
        <div>
          <h1 className="param-title">Devis</h1>
          <p className="param-subtitle">
            Mentions et conditions appliquées à l&apos;ensemble des devis générés.
          </p>
        </div>
      </div>

      <div className="param-card param-card-fill">
        <div className="param-card-header">
          <span>Paramètres de devis</span>
        </div>

        {loading && <div className="param-state">Chargement...</div>}
        {!loading && error && !parametres && (
          <div className="param-state param-state-error">{error}</div>
        )}

        {!loading && parametres && (
          <form className="param-form param-card-body param-card-body-defilante" onSubmit={handleSubmit}>
            {/* En tête de fiche, et collée là pendant le défilement : sous le dernier champ, on ne la voyait pas. */}
            {error && <div className="param-form-message param-form-message-error">{error}</div>}

            <RemplirParTexte
              variante="param"
              contexte="réglages des devis : TVA, validité, mentions légales, conditions de règlement, notifications, format d'envoi du contrat"
              champs={[
                { cle: 'tauxTva', libelle: 'Taux de TVA (%)', type: 'nombre' },
                { cle: 'dureeValiditeJours', libelle: 'Validité du devis (jours)', type: 'nombre' },
                { cle: 'mentionsLegales', libelle: 'Mentions légales' },
                { cle: 'emailNotifications', libelle: 'Adresse des notifications clients', type: 'email' },
                {
                  cle: 'contratFormatEnvoi',
                  libelle: "Format d'envoi du contrat",
                  type: 'choix',
                  options: [
                    { valeur: 'pdf', libelle: 'PDF (avec le bloc de signatures)' },
                    { valeur: 'docx', libelle: 'Word (.docx, retouchable par le client)' },
                  ],
                },
                { cle: 'conditionsReglement', libelle: 'Conditions de règlement' },
              ]}
              onValeurs={(v) => {
                // Les <input type="number"> de ce formulaire portent des chaînes.
                const suite = Object.fromEntries(
                  Object.entries(v).map(([cle, valeur]) => [cle, String(valeur)])
                );
                setParametres((prev) => ({ ...prev, ...suite }));
                setSuccess(null);
              }}
            />

            <div className="param-form-row">
              <label className="param-field">
                <span className="param-label">Taux de TVA (%)</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className="param-input"
                  value={parametres.tauxTva}
                  onChange={handleChange('tauxTva')}
                  required
                />
              </label>

              <label className="param-field">
                <span className="param-label">Validité du devis (jours)</span>
                <input
                  type="number"
                  min="1"
                  className="param-input"
                  value={parametres.dureeValiditeJours}
                  onChange={handleChange('dureeValiditeJours')}
                  required
                />
              </label>
            </div>

            <label className="param-field">
              <span className="param-label">Mentions légales</span>
              <textarea
                className="param-textarea"
                rows={4}
                value={parametres.mentionsLegales}
                onChange={handleChange('mentionsLegales')}
                required
              />
            </label>

            <label className="param-field">
              <span className="param-label">Adresse des notifications clients</span>
              {/* Chaque geste d'un client (signature, refus, dépôt) y est
                  signalé par email. Vide : la cloche seule, et elle le dit. */}
              <input
                type="email"
                className="param-input"
                placeholder="notifications@echo-cse.fr"
                value={parametres.emailNotifications || ''}
                onChange={handleChange('emailNotifications')}
              />
            </label>

            <label className="param-field">
              <span className="param-label">Format d&apos;envoi du contrat</span>
              {/* La pièce jointe de l'email d'envoi du contrat. Le PDF porte le
                  bloc de signatures ; le Word, non — la signature se fait en ligne. */}
              <SelectRecherche
                className="param-select"
                value={parametres.contratFormatEnvoi || 'pdf'}
                onChange={handleChange('contratFormatEnvoi')}
              >
                <option value="pdf">PDF (avec le bloc de signatures)</option>
                <option value="docx">Word (.docx, retouchable par le client)</option>
              </SelectRecherche>
            </label>

            <label className="param-field">
              <span className="param-label">Conditions de règlement</span>
              <textarea
                className="param-textarea"
                rows={4}
                value={parametres.conditionsReglement}
                onChange={handleChange('conditionsReglement')}
                required
              />
            </label>

            {success && (
              <div className="param-form-message param-form-message-success">{success}</div>
            )}

            <div className="param-form-actions">
              <button type="submit" className="param-btn param-btn-primary" disabled={saving}>
                {saving ? 'Enregistrement' : 'Enregistrer'}
              </button>
            </div>
          </form>
        )}
      </div>

      <ReinitialisationDemandes />
    </div>
  );
}

export default ParametrageDevis;
